/**
 * Language utility functions for browser language detection and validation
 */

/**
 * Gets the browser's preferred language and returns a supported language code.
 * Used during onboarding to set the user's default language based on their device.
 * 
 * @returns 'en' | 'es' - Returns 'en' or 'es' if supported, otherwise defaults to 'en'
 */
export function getDefaultLanguageForOnboarding(): string {
  try {
    // Get browser language preference
    const browserLang = navigator.language || navigator.languages?.[0] || 'es';
    
    // Extract language code (e.g., 'en-US' → 'en', 'es-ES' → 'es')
    const langCode = browserLang.split('-')[0].toLowerCase();
    
    // Return supported language or default to English
    return ['en', 'es'].includes(langCode) ? langCode : 'es';
  } catch (error) {
    // Fallback to English if any error occurs
    console.warn('Error detecting browser language:', error);
    return 'es';
  }
}

/**
 * Validates if a language code is supported by the application
 * 
 * @param language - Language code to validate
 * @returns boolean - True if language is supported
 */
export function isValidLanguage(language: string): boolean {
  return ['en', 'es'].includes(language);
}

/**
 * Gets the user's language preference with fallback logic:
 * 1. User's Firestore language (if authenticated and available)
 * 2. localStorage language (if valid)
 * 3. Browser detected language (if valid)
 * 4. Default to 'en'
 * 
 * @param userLanguage - Language from user's Firestore data
 * @returns string - The language code to use
 */
export function getUserLanguagePreference(userLanguage?: string): string {
  // Priority 1: User's stored language in Firestore
  if (userLanguage && isValidLanguage(userLanguage)) {
    return userLanguage;
  }
  
  // Priority 2: localStorage language
  const storedLanguage = localStorage.getItem('i18nextLng');
  if (storedLanguage && isValidLanguage(storedLanguage)) {
    return storedLanguage;
  }
  
  // Priority 3: Browser language detection
  return getDefaultLanguageForOnboarding();
}